import React, { Component, ErrorInfo, ReactNode } from 'react';

import { getTelemetry } from '~/sdk/client';

interface AppErrorBoundaryProps {
  children: ReactNode;
  resetKey: string;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
  componentStack: string | null;
  errorId: string | null;
  errorRoute: string | null;
  errorTimestamp: string | null;
  isChunkError: boolean;
  autoRetrying: boolean;
  diagnosticCopied: boolean;
}

const CHUNK_RETRY_STORAGE_KEY = 'lala:app-error-boundary:chunk-retry';
const CHUNK_ERROR_PATTERN =
  /chunkloaderror|loading chunk|loading css chunk|dynamically imported module|chunk load|failed to fetch dynamically/i;

function buildErrorId(message: string, route: string): string {
  const input = `${message}::${route}`;
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) >>> 0;
  }
  return hash.toString(36).toUpperCase().padStart(6, '0').slice(-6);
}

function isChunkLoadError(error: Error, info: ErrorInfo): boolean {
  const haystack = `${error?.name ?? ''} ${error?.message ?? ''} ${error?.stack ?? ''} ${
    info?.componentStack ?? ''
  }`;
  return CHUNK_ERROR_PATTERN.test(haystack);
}

function alreadyRetriedChunk(errorId: string): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return true;
    return window.sessionStorage.getItem(`${CHUNK_RETRY_STORAGE_KEY}:${errorId}`) === '1';
  } catch {
    return true;
  }
}

function markChunkRetried(errorId: string) {
  try {
    window.sessionStorage.setItem(`${CHUNK_RETRY_STORAGE_KEY}:${errorId}`, '1');
  } catch {
    // sessionStorage puede no estar disponible (modo privado): no bloquea el fallback.
  }
}

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
    errorMessage: null,
    componentStack: null,
    errorId: null,
    errorRoute: null,
    errorTimestamp: null,
    isChunkError: false,
    autoRetrying: false,
    diagnosticCopied: false,
  };

  private copiedFeedbackTimeout: number | null = null;

  static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const route =
      this.props.resetKey || (typeof window !== 'undefined' ? window.location.pathname : 'unknown');
    const message = error?.message || 'Unknown render error';
    const errorId = buildErrorId(message, route);
    const chunkError = isChunkLoadError(error, info);

    this.setState({
      hasError: true,
      errorMessage: message,
      componentStack: info?.componentStack ?? null,
      errorId,
      errorRoute: route,
      errorTimestamp: new Date().toISOString(),
      isChunkError: chunkError,
    });

    // Solo en desarrollo imprimimos el stack en consola (en prod va a telemetría).
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[AppErrorBoundary]', { errorId, route, error, info });
    }

    try {
      const telemetry = getTelemetry();
      telemetry?.recordError({
        message: `[AppErrorBoundary:${errorId}] ${message}`,
        stack: error?.stack ?? null,
        kind: 'react-error-boundary',
      });
      telemetry?.track('app_error_boundary', {
        errorId,
        route,
        chunkError,
        message: message.slice(0, 300),
        componentStack: (info?.componentStack ?? '').slice(0, 2000),
      });
      telemetry?.flush(false);
    } catch {
      // Telemetría es best-effort: nunca debe romper el fallback.
    }

    // Chunks viejos tras un deploy (hash que ya no existe): un reload lo arregla.
    // Se reintenta una sola vez por errorId para evitar bucles infinitos.
    if (chunkError && typeof window !== 'undefined' && !alreadyRetriedChunk(errorId)) {
      markChunkRetried(errorId);
      this.setState({ autoRetrying: true });
      window.setTimeout(() => window.location.reload(), 800);
    }
  }

  componentDidUpdate(previousProps: AppErrorBoundaryProps) {
    if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
      this.setState({
        hasError: false,
        errorMessage: null,
        componentStack: null,
        errorId: null,
        errorRoute: null,
        errorTimestamp: null,
        isChunkError: false,
        autoRetrying: false,
        diagnosticCopied: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.copiedFeedbackTimeout !== null) {
      window.clearTimeout(this.copiedFeedbackTimeout);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      errorMessage: null,
      componentStack: null,
      errorId: null,
      errorRoute: null,
      errorTimestamp: null,
      isChunkError: false,
      autoRetrying: false,
      diagnosticCopied: false,
    });
  };

  private copyWithFallback = (text: string): boolean => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  private showCopiedFeedback = () => {
    this.setState({ diagnosticCopied: true });
    if (this.copiedFeedbackTimeout !== null) {
      window.clearTimeout(this.copiedFeedbackTimeout);
    }
    this.copiedFeedbackTimeout = window.setTimeout(() => {
      this.setState({ diagnosticCopied: false });
      this.copiedFeedbackTimeout = null;
    }, 3000);
  };

  private handleCopyDiagnostic = async () => {
    const { errorId, errorRoute, errorMessage, componentStack, errorTimestamp } = this.state;
    const diagnostic = [
      `errorId: ${errorId ?? 'unknown'}`,
      `route: ${errorRoute ?? 'unknown'}`,
      `message: ${errorMessage ?? 'Unknown render error'}`,
      `componentStack: ${componentStack ?? 'Not available'}`,
      `userAgent: ${navigator.userAgent}`,
      `timestamp: ${errorTimestamp ?? new Date().toISOString()}`,
    ].join('\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(diagnostic);
      } else if (!this.copyWithFallback(diagnostic)) {
        return;
      }
      this.showCopiedFeedback();
    } catch {
      // Algunos navegadores exponen Clipboard API pero la bloquean: usamos el fallback móvil.
      if (this.copyWithFallback(diagnostic)) {
        this.showCopiedFeedback();
      }
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { errorId, errorMessage, isChunkError, autoRetrying, diagnosticCopied } = this.state;

    return (
      <main
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f5f5f5',
        }}
      >
        <section
          style={{
            width: '100%',
            maxWidth: 480,
            padding: 32,
            borderRadius: 12,
            background: '#fff',
            textAlign: 'center',
          }}
        >
          <h1 style={{ marginBottom: 12 }}>No pudimos mostrar esta sección</h1>
          <p style={{ marginBottom: 16 }}>
            {autoRetrying || isChunkError
              ? 'Detectamos una actualización pendiente y estamos recargando automáticamente. Si no ocurre, usa Recargar.'
              : 'Puedes volver al inicio o recargar la página para intentarlo nuevamente.'}
          </p>
          {errorId && (
            <p style={{ marginBottom: 8, fontSize: 13, color: '#555' }}>
              Código de error: <code>{errorId}</code>
              {this.props.resetKey ? ` · Ruta: ${this.props.resetKey}` : null}
            </p>
          )}
          {errorMessage && (
            <details style={{ marginBottom: 16, fontSize: 13, color: '#555' }}>
              <summary style={{ cursor: 'pointer' }}>Ver detalle técnico</summary>
              <p
                style={{
                  marginTop: 8,
                  maxHeight: 180,
                  overflow: 'auto',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {errorMessage}
              </p>
            </details>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
            <a className="btn btn-outline btn-rounded" href="/">
              Ir al inicio
            </a>
            <button
              className="btn btn-outline btn-rounded"
              type="button"
              onClick={this.handleRetry}
            >
              Reintentar
            </button>
            <button
              className="btn btn-dark btn-rounded"
              type="button"
              onClick={() => window.location.reload()}
            >
              Recargar
            </button>
            <button
              className="btn btn-outline btn-rounded"
              type="button"
              onClick={this.handleCopyDiagnostic}
            >
              {diagnosticCopied ? 'Copiado' : 'Copiar diagnóstico'}
            </button>
          </div>
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
